package com.sddamico.pages

import androidx.compose.runtime.*
import com.sddamico.components.widgets.Modal
import com.sddamico.components.widgets.TogglePill
import com.sddamico.HeadingFontFamily
import com.sddamico.SitePalette
import com.sddamico.api.ApiException
import com.sddamico.api.SuccessResponse
import com.sddamico.api.apiGet
import com.sddamico.api.apiJson
import com.sddamico.api.apiPost
import com.sddamico.components.layouts.PageLayoutData
import com.sddamico.components.widgets.ConfirmableButton
import com.sddamico.models.ChangeEmailRequest
import com.sddamico.models.UpdateSettingsRequest
import com.sddamico.models.VapidKeyResponse
import com.sddamico.models.VerifyEmailChangeRequest
import com.sddamico.push.getPermissionState
import com.sddamico.push.isPushSupported
import com.sddamico.state.LocalAuthActions
import com.sddamico.state.LocalAuthState
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.launch
import kotlinx.serialization.encodeToString
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Text

@InitRoute
fun initSettings(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData("Settings"))
}

@Page
@Layout(".components.layouts.PageLayout")
@Composable
fun SettingsPage() {
    val authState = LocalAuthState.current
    val actions = LocalAuthActions.current
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()

    // Auth guard
    LaunchedEffect(authState.loading, authState.user) {
        if (!authState.loading && authState.user == null) {
            ctx.router.tryRoutingTo("/sign-in?returnTo=/settings")
        }
        if (authState.user?.isImpersonating == true) {
            ctx.router.tryRoutingTo("/clubhouse")
        }
    }

    if (authState.loading || authState.user == null) return

    val user = authState.user!!

    var ownerName by remember(user.fullName) { mutableStateOf(user.fullName) }
    var teamName by remember(user.clubName) { mutableStateOf(user.clubName ?: "") }
    var savingName by remember { mutableStateOf(false) }
    var savingTeam by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var showEmailModal by remember { mutableStateOf(false) }
    var emailSuccess by remember { mutableStateOf<String?>(null) }

    // Push notification state
    val pushSupported = remember { isPushSupported() }
    var pushAvailable by remember { mutableStateOf(false) }
    var pushPermissionDenied by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) {
        if (pushSupported) {
            // Check if server has VAPID keys configured
            try {
                apiGet("/api/push/vapid-key", VapidKeyResponse.serializer())
                pushAvailable = true
            } catch (_: Exception) {
                // Server doesn't support push — hide the section
                return@LaunchedEffect
            }
            pushPermissionDenied = getPermissionState() == "denied"
        }
    }

    val nameChanged = ownerName != user.fullName
    val teamChanged = teamName != (user.clubName ?: "")

    Column(
        Modifier
            .fillMaxWidth()
            .backgroundColor(SitePalette.surface)
            .border(1.px, LineStyle.Solid, SitePalette.border)
            .borderRadius(8.px)
            .padding(1.5.cssRem)
            .gap(1.25.cssRem)
    ) {
            // Heading
            SpanText(
                "Settings",
                Modifier
                    .fontFamily(*HeadingFontFamily)
                    .fontSize(1.25.cssRem)
                    .fontWeight(FontWeight.Bold)
                    .color(SitePalette.text)
            )

            Div(
                Modifier
                    .fillMaxWidth()
                    .height(1.px)
                    .backgroundColor(SitePalette.border)
                    .toAttrs()
            )

            // Owner Name field
            Column(Modifier.fillMaxWidth().gap(0.375.cssRem)) {
                SpanText(
                    "Owner Name",
                    Modifier
                        .fontSize(0.8125.cssRem)
                        .fontWeight(FontWeight.Medium)
                        .color(SitePalette.textSecondary)
                )
                Row(
                    Modifier
                        .fillMaxWidth()
                        .position(Position.Relative)
                        .gap(0.5.cssRem),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Input(InputType.Text) {
                        value(ownerName)
                        onInput { ownerName = it.value }
                        style {
                            property("flex", "1")
                            property("padding", "0.75rem")
                            property("background-color", "#0F1216")
                            property("border", "1px solid #2A3038")
                            property("border-radius", "8px")
                            property("color", "#E8E6E1")
                            property("font-size", "0.9375rem")
                            property("outline", "none")
                        }
                    }
                    ConfirmableButton(
                        label = "Save",
                        loadingLabel = "Saving...",
                        confirmLabel = "Are you sure?",
                        isLoading = savingName,
                        enabled = nameChanged && ownerName.isNotBlank(),
                        buttonModifier = Modifier
                            .backgroundColor(if (nameChanged && ownerName.isNotBlank()) SitePalette.primary else SitePalette.border)
                            .color(Colors.White)
                            .borderRadius(8.px)
                            .padding(topBottom = 0.625.cssRem, leftRight = 1.cssRem)
                            .fontSize(0.8125.cssRem)
                            .fontWeight(FontWeight.SemiBold)
                            .opacity(if (nameChanged && ownerName.isNotBlank()) 1.0 else 0.5),
                        onConfirm = {
                            scope.launch {
                                savingName = true
                                error = null
                                try {
                                    val body = apiJson.encodeToString(UpdateSettingsRequest(fullName = ownerName.trim()))
                                    apiPost("/api/settings/update", body, SuccessResponse.serializer())
                                    actions.refresh()
                                } catch (e: ApiException) {
                                    error = e.errorMessage
                                } catch (_: Exception) {
                                    error = "Failed to update name"
                                }
                                savingName = false
                            }
                        }
                    )
                }
            }

            // Team Name field
            if (user.clubName != null) {
                Column(Modifier.fillMaxWidth().gap(0.375.cssRem)) {
                    SpanText(
                        "Club Name",
                        Modifier
                            .fontSize(0.8125.cssRem)
                            .fontWeight(FontWeight.Medium)
                            .color(SitePalette.textSecondary)
                    )
                    Row(
                        Modifier
                            .fillMaxWidth()
                            .position(Position.Relative)
                            .gap(0.5.cssRem),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Input(InputType.Text) {
                            value(teamName)
                            onInput { teamName = it.value }
                            style {
                                property("flex", "1")
                                property("padding", "0.75rem")
                                property("background-color", "#0F1216")
                                property("border", "1px solid #2A3038")
                                property("border-radius", "8px")
                                property("color", "#E8E6E1")
                                property("font-size", "0.9375rem")
                                property("outline", "none")
                            }
                        }
                        ConfirmableButton(
                            label = "Save",
                            loadingLabel = "Saving...",
                            confirmLabel = "Are you sure?",
                            isLoading = savingTeam,
                            enabled = teamChanged && teamName.isNotBlank(),
                            buttonModifier = Modifier
                                .backgroundColor(if (teamChanged && teamName.isNotBlank()) SitePalette.primary else SitePalette.border)
                                .color(Colors.White)
                                .borderRadius(8.px)
                                .padding(topBottom = 0.625.cssRem, leftRight = 1.cssRem)
                                .fontSize(0.8125.cssRem)
                                .fontWeight(FontWeight.SemiBold)
                                .opacity(if (teamChanged && teamName.isNotBlank()) 1.0 else 0.5),
                            onConfirm = {
                                scope.launch {
                                    savingTeam = true
                                    error = null
                                    try {
                                        val body = apiJson.encodeToString(UpdateSettingsRequest(clubName = teamName.trim()))
                                        apiPost("/api/settings/update", body, SuccessResponse.serializer())
                                        actions.refresh()
                                    } catch (e: ApiException) {
                                        error = e.errorMessage
                                    } catch (_: Exception) {
                                        error = "Failed to update club name"
                                    }
                                    savingTeam = false
                                }
                            }
                        )
                    }
                }
            }

            // Error message
            error?.let {
                SpanText(
                    it,
                    Modifier
                        .color(SitePalette.error)
                        .fontSize(0.8125.cssRem)
                )
            }

            // Divider
            Div(
                Modifier
                    .fillMaxWidth()
                    .height(1.px)
                    .backgroundColor(SitePalette.border)
                    .toAttrs()
            )

            // Notifications section
            Column(Modifier.fillMaxWidth().gap(0.5.cssRem)) {
                SpanText(
                    "Notifications",
                    Modifier
                        .fontFamily(*HeadingFontFamily)
                        .fontSize(1.25.cssRem)
                        .fontWeight(FontWeight.Bold)
                        .color(SitePalette.text)
                )

                NotificationToggle(
                    label = "Roster Changes",
                    description = "Player adds, drops, and claims",
                    checked = user.emailRosterChanges,
                    onToggle = { newValue ->
                        scope.launch {
                            error = null
                            try {
                                val body = apiJson.encodeToString(UpdateSettingsRequest(emailRosterChanges = newValue))
                                apiPost("/api/settings/update", body, SuccessResponse.serializer())
                                actions.refresh()
                            } catch (_: Exception) {
                                error = "Failed to update preference"
                            }
                        }
                    }
                )

                NotificationToggle(
                    label = "Weekend Recap",
                    description = "Monday morning tournament summary",
                    checked = user.emailWeekendRecap,
                    onToggle = { newValue ->
                        scope.launch {
                            error = null
                            try {
                                val body = apiJson.encodeToString(UpdateSettingsRequest(emailWeekendRecap = newValue))
                                apiPost("/api/settings/update", body, SuccessResponse.serializer())
                                actions.refresh()
                            } catch (_: Exception) {
                                error = "Failed to update preference"
                            }
                        }
                    }
                )

                NotificationToggle(
                    label = "Waiver Reports",
                    description = "Waiver claim processing results",
                    checked = user.emailWaiverReports,
                    onToggle = { newValue ->
                        scope.launch {
                            error = null
                            try {
                                val body = apiJson.encodeToString(UpdateSettingsRequest(emailWaiverReports = newValue))
                                apiPost("/api/settings/update", body, SuccessResponse.serializer())
                                actions.refresh()
                            } catch (_: Exception) {
                                error = "Failed to update preference"
                            }
                        }
                    }
                )

                NotificationToggle(
                    label = "Pick Trades",
                    description = "Draft pick swaps between clubs",
                    checked = user.emailPickTrades,
                    onToggle = { newValue ->
                        scope.launch {
                            error = null
                            try {
                                val body = apiJson.encodeToString(UpdateSettingsRequest(emailPickTrades = newValue))
                                apiPost("/api/settings/update", body, SuccessResponse.serializer())
                                actions.refresh()
                            } catch (_: Exception) {
                                error = "Failed to update preference"
                            }
                        }
                    }
                )
            }

            // Push Notifications section
            if (pushAvailable) {
                Div(
                    Modifier
                        .fillMaxWidth()
                        .height(1.px)
                        .backgroundColor(SitePalette.border)
                        .toAttrs()
                )

                Column(Modifier.fillMaxWidth().gap(0.5.cssRem)) {
                    SpanText(
                        "Push Notifications",
                        Modifier
                            .fontFamily(*HeadingFontFamily)
                            .fontSize(1.25.cssRem)
                            .fontWeight(FontWeight.Bold)
                            .color(SitePalette.text)
                    )

                    if (pushPermissionDenied) {
                        SpanText(
                            "Push notifications are blocked in your browser settings",
                            Modifier
                                .fontSize(0.8125.cssRem)
                                .color(SitePalette.textMuted)
                                .padding(topBottom = 0.625.cssRem, leftRight = 0.75.cssRem)
                        )
                    } else {
                        NotificationToggle(
                            label = "Tournament Start",
                            description = "When a scored tournament begins",
                            checked = user.pushTournamentStart,
                            onToggle = { newValue ->
                                scope.launch {
                                    error = null
                                    try {
                                        val body = apiJson.encodeToString(UpdateSettingsRequest(pushTournamentStart = newValue))
                                        apiPost("/api/settings/update", body, SuccessResponse.serializer())
                                        actions.refresh()
                                    } catch (_: Exception) {
                                        error = "Failed to update preference"
                                    }
                                }
                            }
                        )

                        NotificationToggle(
                            label = "Tournament Results",
                            description = "When a scored tournament finishes",
                            checked = user.pushTournamentComplete,
                            onToggle = { newValue ->
                                scope.launch {
                                    error = null
                                    try {
                                        val body = apiJson.encodeToString(UpdateSettingsRequest(pushTournamentComplete = newValue))
                                        apiPost("/api/settings/update", body, SuccessResponse.serializer())
                                        actions.refresh()
                                    } catch (_: Exception) {
                                        error = "Failed to update preference"
                                    }
                                }
                            }
                        )

                        NotificationToggle(
                            label = "Made the Cut",
                            description = "When your players survive the cut in a golf tournament",
                            checked = user.pushMadeTheCut,
                            onToggle = { newValue ->
                                scope.launch {
                                    error = null
                                    try {
                                        val body = apiJson.encodeToString(UpdateSettingsRequest(pushMadeTheCut = newValue))
                                        apiPost("/api/settings/update", body, SuccessResponse.serializer())
                                        actions.refresh()
                                    } catch (_: Exception) {
                                        error = "Failed to update preference"
                                    }
                                }
                            }
                        )

                        NotificationToggle(
                            label = "Tennis Round Advancement",
                            description = "When your player advances in a tennis tournament",
                            checked = user.pushTennisAdvancement,
                            onToggle = { newValue ->
                                scope.launch {
                                    error = null
                                    try {
                                        val body = apiJson.encodeToString(UpdateSettingsRequest(pushTennisAdvancement = newValue))
                                        apiPost("/api/settings/update", body, SuccessResponse.serializer())
                                        actions.refresh()
                                    } catch (_: Exception) {
                                        error = "Failed to update preference"
                                    }
                                }
                            }
                        )

                        NotificationToggle(
                            label = "Roster Transactions",
                            description = "Player adds, drops, claims, and waivers",
                            checked = user.pushRosterChanges,
                            onToggle = { newValue ->
                                scope.launch {
                                    error = null
                                    try {
                                        val body = apiJson.encodeToString(UpdateSettingsRequest(pushRosterChanges = newValue))
                                        apiPost("/api/settings/update", body, SuccessResponse.serializer())
                                        actions.refresh()
                                    } catch (_: Exception) {
                                        error = "Failed to update preference"
                                    }
                                }
                            }
                        )

                        NotificationToggle(
                            label = "Pick Trades",
                            description = "Draft pick swaps between clubs",
                            checked = user.pushPickTrades,
                            onToggle = { newValue ->
                                scope.launch {
                                    error = null
                                    try {
                                        val body = apiJson.encodeToString(UpdateSettingsRequest(pushPickTrades = newValue))
                                        apiPost("/api/settings/update", body, SuccessResponse.serializer())
                                        actions.refresh()
                                    } catch (_: Exception) {
                                        error = "Failed to update preference"
                                    }
                                }
                            }
                        )
                    }
                }
            }

            // Divider
            Div(
                Modifier
                    .fillMaxWidth()
                    .height(1.px)
                    .backgroundColor(SitePalette.border)
                    .toAttrs()
            )

            // Account section
            Column(Modifier.fillMaxWidth().gap(0.75.cssRem)) {
                SpanText(
                    "Account",
                    Modifier
                        .fontFamily(*HeadingFontFamily)
                        .fontSize(1.25.cssRem)
                        .fontWeight(FontWeight.Bold)
                        .color(SitePalette.text)
                )

                Row(
                    Modifier.gap(0.5.cssRem),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    SpanText(
                        user.email,
                        Modifier
                            .fontSize(0.875.cssRem)
                            .color(SitePalette.textMuted)
                    )
                    SpanText(
                        "Edit",
                        Modifier
                            .fontSize(0.8125.cssRem)
                            .fontWeight(FontWeight.Medium)
                            .color(SitePalette.accent)
                            .cursor(Cursor.Pointer)
                            .onClick {
                                emailSuccess = null
                                showEmailModal = true
                            }
                    )
                }

                emailSuccess?.let {
                    SpanText(
                        it,
                        Modifier
                            .color(SitePalette.primary)
                            .fontSize(0.8125.cssRem)
                    )
                }

                Div(Modifier.height(0.5.cssRem).toAttrs())

                Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                    Spacer()
                    Div(
                        Modifier
                            .backgroundColor(Colors.Transparent)
                            .border(1.px, LineStyle.Solid, SitePalette.error)
                            .borderRadius(8.px)
                            .padding(topBottom = 0.75.cssRem, leftRight = 1.5.cssRem)
                            .color(SitePalette.error)
                            .fontSize(0.875.cssRem)
                            .fontWeight(FontWeight.SemiBold)
                            .cursor(Cursor.Pointer)
                            .transition(Transition.of("background-color", 150.ms))
                            .onClick {
                                scope.launch {
                                    actions.logout()
                                    ctx.router.tryRoutingTo("/")
                                }
                            }
                            .toAttrs()
                    ) {
                        Text("Log Out")
                    }
                }
            }
        }

    if (showEmailModal) {
        ChangeEmailModal(
            onClose = { showEmailModal = false },
            onSuccess = { newEmail ->
                showEmailModal = false
                emailSuccess = "Email updated to $newEmail"
                scope.launch { actions.refresh() }
            }
        )
    }
}

@Composable
private fun NotificationToggle(
    label: String,
    description: String,
    checked: Boolean,
    onToggle: (Boolean) -> Unit,
) {
    Row(
        Modifier
            .fillMaxWidth()
            .padding(topBottom = 0.625.cssRem, leftRight = 0.75.cssRem)
            .borderRadius(8.px)
            .cursor(Cursor.Pointer)
            .transition(Transition.of("background-color", 150.ms))
            .onClick { onToggle(!checked) },
        verticalAlignment = Alignment.CenterVertically
    ) {
        Column(Modifier.styleModifier { property("flex", "1") }.gap(0.125.cssRem)) {
            SpanText(
                label,
                Modifier
                    .fontSize(0.875.cssRem)
                    .fontWeight(FontWeight.Medium)
                    .color(SitePalette.text)
            )
            SpanText(
                description,
                Modifier
                    .fontSize(0.75.cssRem)
                    .color(SitePalette.textMuted)
            )
        }
        TogglePill(checked)
    }
}

@Composable
private fun ChangeEmailModal(
    onClose: () -> Unit,
    onSuccess: (String) -> Unit,
) {
    val scope = rememberCoroutineScope()

    var step by remember { mutableStateOf(1) }
    var newEmail by remember { mutableStateOf("") }
    var code by remember { mutableStateOf("") }
    var loading by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }

    Modal(onDismiss = onClose) {
        val submitStep1: () -> Unit = {
            if (!loading && newEmail.isNotBlank()) {
                scope.launch {
                    loading = true
                    error = null
                    try {
                        val body = apiJson.encodeToString(ChangeEmailRequest(newEmail = newEmail.trim()))
                        apiPost("/api/settings/send-email-change-otp", body, SuccessResponse.serializer())
                        step = 2
                    } catch (e: ApiException) {
                        error = e.errorMessage
                    } catch (_: Exception) {
                        error = "Something went wrong"
                    }
                    loading = false
                }
            }
        }

        val submitStep2: () -> Unit = {
            if (!loading && code.length == 6) {
                scope.launch {
                    loading = true
                    error = null
                    try {
                        val body = apiJson.encodeToString(VerifyEmailChangeRequest(newEmail = newEmail.trim(), code = code.trim()))
                        apiPost("/api/settings/verify-email-change", body, SuccessResponse.serializer())
                        val email = newEmail.trim()
                        dismiss { onSuccess(email) }
                    } catch (e: ApiException) {
                        error = e.errorMessage
                    } catch (_: Exception) {
                        error = "Something went wrong"
                    }
                    loading = false
                }
            }
        }

        Column(Modifier.fillMaxWidth().gap(1.25.cssRem)) {
            // Header
            SpanText(
                "Change Email",
                Modifier
                    .fontSize(1.25.cssRem)
                    .fontWeight(FontWeight.Bold)
                    .color(SitePalette.text)
            )

            SpanText(
                if (step == 1) "Enter your new email address"
                else "Code sent to $newEmail",
                Modifier
                    .fontSize(0.8125.cssRem)
                    .color(SitePalette.textSecondary)
            )

            if (step == 1) {
                Input(InputType.Email) {
                    placeholder("new@example.com")
                    value(newEmail)
                    onInput { newEmail = it.value }
                    onKeyDown { if (it.key == "Enter") submitStep1() }
                    style {
                        property("width", "100%")
                        property("padding", "0.75rem")
                        property("background-color", "#0F1216")
                        property("border", "1px solid #2A3038")
                        property("border-radius", "8px")
                        property("color", "#E8E6E1")
                        property("font-size", "0.9375rem")
                        property("outline", "none")
                    }
                }
            } else {
                Input(InputType.Text) {
                    placeholder("000000")
                    value(code)
                    onInput {
                        val filtered = it.value.filter { c -> c.isDigit() }.take(6)
                        code = filtered
                        if (filtered.length == 6) submitStep2()
                    }
                    onKeyDown { if (it.key == "Enter") submitStep2() }
                    attr("maxlength", "6")
                    attr("autocomplete", "one-time-code")
                    style {
                        property("width", "100%")
                        property("padding", "0.75rem")
                        property("background-color", "#0F1216")
                        property("border", "1px solid #2A3038")
                        property("border-radius", "8px")
                        property("color", "#E8E6E1")
                        property("font-size", "1.5rem")
                        property("text-align", "center")
                        property("letter-spacing", "0.5rem")
                        property("outline", "none")
                        property("font-variant-numeric", "tabular-nums")
                    }
                }
            }

            // Error
            error?.let {
                SpanText(
                    it,
                    Modifier
                        .color(SitePalette.error)
                        .fontSize(0.8125.cssRem)
                )
            }

            // Buttons
            Row(Modifier.fillMaxWidth().gap(0.75.cssRem), verticalAlignment = Alignment.CenterVertically) {
                Spacer()

                // Cancel
                Div(
                    Modifier
                        .backgroundColor(SitePalette.border)
                        .color(SitePalette.text)
                        .borderRadius(8.px)
                        .padding(topBottom = 0.5.cssRem, leftRight = 1.cssRem)
                        .fontSize(0.875.cssRem)
                        .fontWeight(FontWeight.Medium)
                        .cursor(Cursor.Pointer)
                        .onClick { dismiss(onClose) }
                        .toAttrs()
                ) {
                    Text("Cancel")
                }

                // Submit
                val canSubmit = if (step == 1) newEmail.isNotBlank() && !loading else code.length == 6 && !loading
                Button(
                    attrs = Modifier
                        .backgroundColor(if (canSubmit) SitePalette.primary else SitePalette.border)
                        .color(if (canSubmit) Colors.White else SitePalette.textMuted)
                        .borderRadius(8.px)
                        .padding(topBottom = 0.5.cssRem, leftRight = 1.cssRem)
                        .fontSize(0.875.cssRem)
                        .fontWeight(FontWeight.SemiBold)
                        .border(0.px)
                        .cursor(if (canSubmit) Cursor.Pointer else Cursor.Default)
                        .toAttrs {
                            if (!canSubmit) disabled()
                            onClick {
                                if (step == 1) submitStep1() else submitStep2()
                            }
                        }
                ) {
                    Text(
                        when {
                            loading && step == 1 -> "Sending..."
                            loading && step == 2 -> "Verifying..."
                            step == 1 -> "Send Code"
                            else -> "Verify Code"
                        }
                    )
                }
            }
        }
    }
}
